import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AuthContext from '../../components/shared/AuthContext';
import PageMeta from '../../components/seo/PageMeta';
import AiResponseRenderer from '../../components/AiResponseRenderer';
import AdaptivePageShell from '../../features/adaptive-learning/AdaptivePageShell';
import { TUTOR_PROMPTS } from '../../features/adaptive-learning/adaptiveUtils';

export default function TutorPage() {
  const { apiClient, userProfile } = useContext(AuthContext);
  const location = useLocation();
  const initialTopic = location.state?.topic || '';
  const [topic, setTopic] = useState(initialTopic);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const threadRef = useRef(null);

  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  const start = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await apiClient.post('/api/v1/tutor/session', { topic: topic.trim() || 'General study' });
      setSessionId(res.data.sessionId);
      setMessages([]);
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not start tutor session. Check AI quota.');
    } finally {
      setBusy(false);
    }
  };

  const sendMessage = async (text) => {
    if (!sessionId || !text.trim() || busy) return;
    const trimmed = text.trim();
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: trimmed }]);
    setBusy(true);
    setError('');
    try {
      const res = await apiClient.post('/api/v1/tutor/message', { sessionId, message: trimmed });
      setMessages((m) => [...m, { role: 'assistant', content: res.data.reply, generated: true }]);
    } catch (e) {
      setError(e?.response?.data?.error || 'Message failed. Try again or start a new session.');
      setMessages((m) => m.slice(0, -1));
      setInput(trimmed);
    } finally {
      setBusy(false);
    }
  };

  const endSession = () => {
    setSessionId(null);
    setMessages([]);
    setError('');
  };

  return (
    <>
      <PageMeta title="AI Tutor" description="Conversational learning tutor with session context." />
      <AdaptivePageShell userProfile={userProfile} backLabel="Study hub">
        <header className="adaptive-hero">
          <h1>AI Tutor</h1>
          <p className="adaptive-disclaimer">
            AI-generated tutoring — verify important facts against trusted sources.
          </p>
          {!sessionId && (
            <div className="tutor-start">
              <input
                className="learn-input"
                placeholder="Topic (e.g. Java Concurrency)"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && start()}
              />
              <button type="button" className="learn-btn learn-btn--primary" disabled={busy} onClick={start}>
                {busy ? 'Starting…' : 'Start session'}
              </button>
            </div>
          )}
        </header>

        {error && <p className="adaptive-error" role="alert">{error}</p>}

        {sessionId && (
          <div className="tutor-session-bar">
            <span>
              Topic: <strong>{topic || 'General study'}</strong>
            </span>
            <button type="button" className="learn-btn learn-btn-secondary" onClick={endSession}>
              End session
            </button>
          </div>
        )}

        {sessionId && messages.length === 0 && (
          <div className="tutor-prompts" role="group" aria-label="Suggested prompts">
            {TUTOR_PROMPTS.map((p) => (
              <button
                key={p}
                type="button"
                className="tutor-prompt-chip"
                disabled={busy}
                onClick={() => sendMessage(p)}
              >
                {p}
              </button>
            ))}
          </div>
        )}

        <div className="tutor-thread" ref={threadRef} aria-live="polite">
          {messages.map((msg, i) => (
            <div key={i} className={`tutor-msg tutor-msg--${msg.role}`}>
              {msg.generated && <span className="tutor-ai-label">AI-generated</span>}
              <AiResponseRenderer content={msg.content} />
            </div>
          ))}
          {busy && messages.length > 0 && messages[messages.length - 1]?.role === 'user' && (
            <p className="tutor-typing">Tutor is thinking…</p>
          )}
        </div>

        {sessionId && (
          <form
            className="tutor-compose"
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage(input);
            }}
          >
            <input
              className="learn-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Explain, quiz me, give an example…"
              disabled={busy}
              aria-label="Message to tutor"
            />
            <button type="submit" className="learn-btn learn-btn--primary" disabled={busy || !input.trim()}>
              Send
            </button>
          </form>
        )}
      </AdaptivePageShell>
    </>
  );
}
